/**
 * Cover and the table of contents stay one sheet each.
 * From the executive summary on, sections share an A4 sheet when the next one
 * fits. A section that is only a little too tall spills its tail onto the next
 * sheet instead of leaving a short page mostly blank.
 */

const TOC_ALIAS = {
    appendixNetwork: "appendix",
    appendixPlanAdmin: "appendix",
    appendixMemberEngagement: "appendixMembers",
    appendixMemberCare: "appendixMembers",
    appendixCareManagement: "appendixMembers",
};

const MIN_ROOM = 96;

function contentSections(root) {
    return [...root.querySelectorAll(".tpl-exec, .tpl-section")].filter(
        (section) => !section.parentElement?.closest(".tpl-exec, .tpl-section"),
    );
}

function footerReserve(root) {
    const probe = root.ownerDocument.createElement("div");
    probe.style.paddingBottom = "40pt";
    root.appendChild(probe);
    const reserve = parseFloat(getComputedStyle(probe).paddingBottom) || 54;
    probe.remove();
    return reserve;
}

function blocksOf(section) {
    return [...section.children].filter((el) => !el.classList.contains("tpl-page-footer"));
}

function hasBody(section) {
    return blocksOf(section).some((el) => !el.matches("h1, h2, h3") && el.offsetHeight > 0);
}

function shellFor(source) {
    const clone = source.cloneNode(false);
    for (const child of source.children) {
        const holdsTable = child.classList?.contains("tpl-data-table")
            || child.classList?.contains("tpl-data-frame")
            || child.classList?.contains("tpl-data")
            || child.classList?.contains("tpl-data-stack")
            || child.querySelector?.(".tpl-data-table, .tpl-data-frame");
        if (!holdsTable) continue;
        const inner = shellFor(child);
        if (inner.childNodes.length || child.classList.contains("tpl-data-frame")) clone.appendChild(inner);
    }
    return clone;
}

function remember(moves, node) {
    if (!moves || !node?.parentNode) return;
    moves.push({ node, parent: node.parentNode, next: node.nextSibling });
}

function placeTail(continuation, source, node, moves) {
    let host = [...continuation.children].find((el) => el.className === source.className);
    if (!host) {
        host = node.classList.contains("tpl-data-table") ? source.cloneNode(false) : shellFor(source);
        continuation.insertBefore(host, continuation.firstChild);
    }
    if (!node.classList.contains("tpl-data-table")) {
        remember(moves, node);
        host.insertBefore(node, host.firstChild);
        return;
    }
    const stackSource = source.querySelector(":scope > .tpl-data-stack");
    let stack = host.querySelector(":scope > .tpl-data-stack");
    if (stackSource && !stack) {
        stack = stackSource.cloneNode(false);
        host.appendChild(stack);
    }
    const frame = node.parentElement.cloneNode(false);
    remember(moves, node);
    frame.appendChild(node);
    const card = continuation.ownerDocument.createElement("div");
    card.className = "tpl-data is-excel-table";
    card.appendChild(frame);
    (stack || host).insertBefore(card, (stack || host).firstChild);
}

function stackIn(block) {
    if (block.classList.contains("tpl-data-stack")) return block;
    return block.querySelector(":scope > .tpl-data-stack, :scope .tpl-data-stack");
}

function hostStack(continuation, sourceStack) {
    const figureSource = sourceStack.closest(".tpl-figure");
    let parent = continuation;
    if (figureSource) {
        parent = [...continuation.children].find((el) => el.className === figureSource.className);
        if (!parent) {
            parent = figureSource.cloneNode(false);
            continuation.insertBefore(parent, continuation.firstChild);
        }
    }
    let stack = [...parent.children].find((el) => el.className === sourceStack.className);
    if (!stack) {
        stack = sourceStack.cloneNode(false);
        parent.insertBefore(stack, parent.firstChild);
    }
    return stack;
}

function moveRow(continuation, table, row, moves) {
    const sourceStack = table.closest(".tpl-data-stack") || table.parentElement;
    const host = hostStack(continuation, sourceStack);
    let card = host.querySelector(":scope > .tpl-data");
    if (!card) {
        const sourceCard = table.closest(".tpl-data");
        const frameSource = table.closest(".tpl-data-frame");
        card = sourceCard ? sourceCard.cloneNode(false) : continuation.ownerDocument.createElement("div");
        const frame = frameSource ? frameSource.cloneNode(false) : continuation.ownerDocument.createElement("div");
        const clone = table.cloneNode(false);
        const head = table.querySelector("thead");
        if (head) clone.appendChild(head.cloneNode(true));
        clone.appendChild(continuation.ownerDocument.createElement("tbody"));
        frame.appendChild(clone);
        card.appendChild(frame);
        host.insertBefore(card, host.firstChild);
    }
    const body = card.querySelector("tbody");
    remember(moves, row);
    body.insertBefore(row, body.firstChild);
}

function columnCount(row) {
    return [...row.cells].reduce((total, cell) => total + cell.colSpan, 0);
}

function moveRatesTail(table, continuation, moves) {
    const rows = [...(table.tBodies[0]?.rows || [])];
    if (rows.length < 2) return false;
    const last = rows[rows.length - 1];
    if (last.classList.contains("is-rates-foot")) {
        let start = rows.length - 1;
        while (start > 0 && rows[start - 1].classList.contains("is-rates-foot")) start -= 1;
        if (start === 0) return false;
        for (let index = rows.length - 1; index >= start; index -= 1) {
            moveRow(continuation, table, rows[index], moves);
        }
        return true;
    }
    let start = rows.length - 1;
    while (start > 0 && !rows[start].querySelector("th[rowspan]")) start -= 1;
    if (start === 0 || !rows[start].querySelector("th[rowspan]")) return false;
    for (let index = rows.length - 1; index >= start; index -= 1) {
        moveRow(continuation, table, rows[index], moves);
    }
    return true;
}

function moveInnerTail(block, continuation, moves) {
    const stack = stackIn(block);
    if (!stack) return false;
    const pieces = [...stack.children];
    if (!pieces.length) return false;
    const last = pieces[pieces.length - 1];
    const table = last.matches?.(".tpl-data-table") ? last : last.querySelector?.(".tpl-data-table");
    if (table?.classList.contains("tpl-rates")) {
        if (moveRatesTail(table, continuation, moves)) return true;
    } else if (table && !table.classList.contains("tpl-cost-compare") && !table.classList.contains("tpl-writewise")) {
        const rows = [...(table.tBodies[0]?.rows || [])];
        if (rows.length > 1 && columnCount(rows[rows.length - 1]) === columnCount(rows[0])) {
            moveRow(continuation, table, rows[rows.length - 1], moves);
            return true;
        }
    }
    if (pieces.length < 2) return false;
    const host = hostStack(continuation, stack);
    remember(moves, last);
    host.insertBefore(last, host.firstChild);
    return true;
}

function pullDanglingLabels(section, continuation, moves) {
    const table = [...section.querySelectorAll(".tpl-data-table")].pop();
    const body = table?.tBodies[0];
    if (!body) return;
    let guard = 0;
    while (guard < 8) {
        const rows = [...body.rows];
        const last = rows[rows.length - 1];
        const dangling = last?.classList.contains("is-aso-section") || last?.classList.contains("is-aso-group");
        if (!dangling) break;
        if (rows.length === 1 && !continuation.querySelector(".tpl-data-table tbody tr")) break;
        moveRow(continuation, table, last, moves);
        guard += 1;
    }
}

function continuationHost(continuation, block) {
    let host = [...continuation.children].find((el) => el.className === block.className && el.hasAttribute("data-stop-loss-body"));
    if (!host) {
        host = block.cloneNode(false);
        host.removeAttribute("data-aso-field");
        const footer = continuation.querySelector(":scope > .tpl-page-footer");
        continuation.insertBefore(host, footer);
    }
    return host;
}

function moveStopLossPiece(block, continuation, moves) {
    if (!block.hasAttribute("data-stop-loss-body")) return false;
    const last = block.lastElementChild;
    if (!last) return false;
    const host = continuationHost(continuation, block);
    const listTag = last.tagName === "UL" || last.tagName === "OL" ? last.tagName : "";
    if (listTag && last.children.length > 1) {
        const item = last.lastElementChild;
        let list = host.firstElementChild?.tagName === listTag ? host.firstElementChild : null;
        if (!list) {
            list = last.cloneNode(false);
            host.insertBefore(list, host.firstChild);
        }
        remember(moves, item);
        list.insertBefore(item, list.firstChild);
        return true;
    }
    remember(moves, last);
    host.insertBefore(last, host.firstChild);
    if (!block.children.length) block.remove();
    return true;
}

function moveTailPiece(section, continuation, moves) {
    const last = blocksOf(section).pop();
    if (!last) return false;
    if (moveInnerTail(last, continuation, moves)) return true;
    if (moveStopLossPiece(last, continuation, moves)) return true;
    if (last.classList.contains("tpl-disclosure") && last.children.length > 1) {
        placeTail(continuation, last, last.children[last.children.length - 1], moves);
        if (!last.children.length) last.remove();
        return true;
    }
    remember(moves, last);
    continuation.insertBefore(last, continuation.firstChild);
    return true;
}

function undoSplit(continuation) {
    const moves = continuation?.__packMoves || [];
    for (const move of [...moves].reverse()) {
        move.parent?.insertBefore(move.node, move.next);
    }
    continuation?.remove();
}

/**
 * Keep the head of `section` inside `room` and return the overflow section.
 * Returns null when the section cannot shrink without leaving a bare heading.
 * @param {HTMLElement} section
 * @param {number} room
 */
function detachOverflow(section, room) {
    if (section.offsetHeight <= room) return null;
    // The trend chart belongs with the savings table. Splitting it off leaves
    // the graph alone on the next sheet when the block is only slightly tall.
    if (section.querySelector(".tpl-five-chart")) return null;
    // Spend and Centivo vs Status Quo are one comparison. Don't park one table
    // on the next sheet.
    if (section.querySelectorAll(".tpl-cost-compare").length > 1) return null;
    const next = section.cloneNode(false);
    next.classList.add("is-pack-split");
    delete next.dataset.proposalSection;
    const moves = [];
    next.__packMoves = moves;
    const footer = section.querySelector(":scope > .tpl-page-footer");
    section.after(next);
    if (footer) next.appendChild(footer.cloneNode(true));

    let guard = 0;
    while (section.offsetHeight > room && guard < 400) {
        guard += 1;
        if (!moveTailPiece(section, next, moves)) break;
    }
    pullDanglingLabels(section, next, moves);

    if (section.offsetHeight > room || !hasBody(section) || !blocksOf(next).length) {
        undoSplit(next);
        return null;
    }
    return next;
}

function setTocPage(root, key, pageNumber) {
    const slot = root.querySelector(`[data-toc-key="${key}"] .tpl-toc-page`);
    if (slot) slot.textContent = String(pageNumber);
}

function stampFooter(footer, pageNumber) {
    const center = footer.querySelector(".tpl-page-footer-center");
    if (!center) return;
    center.textContent = center.textContent.replace(/Page\s+\d+/, `Page ${pageNumber}`);
}

/**
 * @param {HTMLElement | null | undefined} root
 */
export function packProposalPages(root) {
    if (!root || root.dataset.packed === "true") return;
    root.classList.add("is-compact-pages");

    const pageHeight = root.querySelector(".tpl-cover, .tpl-aso-cover")?.offsetHeight || 0;
    if (pageHeight < 40) return;

    const budget = pageHeight - footerReserve(root) - 4;
    const groups = [];
    let current = [];
    let used = 0;

    const take = (section) => {
        current.push(section);
        used += section.offsetHeight;
    };

    const flush = () => {
        if (!current.length) return;
        groups.push(current);
        current = [];
        used = 0;
    };

    for (const section of contentSections(root)) {
        let rest = section;
        let spins = 0;
        while (rest && spins < 12) {
            spins += 1;
            const height = rest.offsetHeight;
            const room = budget - used;
            if (!current.length) {
                if (height <= budget) {
                    take(rest);
                    rest = null;
                } else {
                    const overflow = detachOverflow(rest, budget);
                    take(rest);
                    flush();
                    rest = overflow;
                }
            } else if (height <= room) {
                take(rest);
                rest = null;
            } else if (room >= MIN_ROOM) {
                const overflow = detachOverflow(rest, room);
                const stranded = overflow?.querySelector(".tpl-data-table tbody tr")
                    && !rest.querySelector(".tpl-data-table tbody tr");
                if (overflow && !stranded) {
                    take(rest);
                    flush();
                    rest = overflow;
                } else {
                    if (overflow) undoSplit(overflow);
                    flush();
                }
            } else {
                flush();
            }
        }
    }
    flush();

    const pageByKey = {};
    let pageNumber = root.querySelector(".tpl-toc") ? 3 : 2;

    for (const group of groups) {
        const wrap = root.ownerDocument.createElement("div");
        const tall = group.length === 1 && group[0].offsetHeight > budget;
        wrap.className = tall ? "tpl-print-page tpl-print-page-tall" : "tpl-print-page";
        group[0].parentNode.insertBefore(wrap, group[0]);

        let footer = null;
        group.forEach((section, index) => {
            section.classList.toggle("tpl-pack-page-start", index === 0);
            const ownFooter = section.querySelector(":scope > .tpl-page-footer");
            if (ownFooter) footer = ownFooter;
            wrap.appendChild(section);
            const keys = [
                section.dataset.proposalSection,
                ...[...section.querySelectorAll("[data-proposal-section]")].map((node) => node.dataset.proposalSection),
            ];
            keys.filter(Boolean).forEach((key) => {
                if (!pageByKey[key]) pageByKey[key] = pageNumber;
            });
        });

        if (footer) {
            footer.classList.add("is-pack-visible");
            stampFooter(footer, pageNumber);
            wrap.appendChild(footer);
        }
        pageNumber += 1;
    }

    root.querySelectorAll(".tpl-data").forEach((card) => {
        if (!card.querySelector(".tpl-data-table tbody tr")) card.remove();
    });

    Object.entries(pageByKey).forEach(([key, page]) => setTocPage(root, key, page));
    Object.entries(TOC_ALIAS).forEach(([alias, source]) => {
        if (pageByKey[source]) setTocPage(root, alias, pageByKey[source]);
    });
    root.dataset.packed = "true";
}
